import React, { useEffect, useState, useCallback } from "react";
import { RiArrowRightSLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { getRequest, putRequest } from "../../helpers/api.helper";
import { getSessionItem } from "../../helpers/function.helper";
import { StepPropsType } from "../../types";
import { heightOptions } from "../../utils/constants";
import { useLanguage } from "../../context/LanguageContext";

type DropdownOption = { value: string; label: string };

const Step2: React.FC<StepPropsType> = ({
  bureauId,
  currentStep,
  setCurrentStep,
  steps,
  profileId,
  setCompletedSteps,
}) => {
  const { t } = useLanguage();
  const [modalData, setModalData] = useState<ModalData | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedOptions, setSelectedOptions] = useState<
    Record<any, DropdownOption>
  >({
    height: { value: "5.5", label: "5.5 ft" },
    weight: { value: "65", label: "65 kg" },
    Physical_State: { value: "normal", label: "Normal" },
    Eating_Habits: { value: "nonVegetarian", label: "Non-Vegetarian" },
    Smoking_Habits: { value: "no", label: "No" },
    Drinking_Habits: { value: "no", label: "No" },
  });

  // Helper functions for defaults
  const getMaxDate = () => {
    const today = new Date();
    const maxDate = new Date(
      today.getFullYear() - 20,
      today.getMonth(),
      today.getDate(),
    );
    return maxDate.toISOString().split("T")[0];
  };
  const getDefaultDate = () => getMaxDate();
  const getCurrentTime = () => new Date().toTimeString().slice(0, 5);

  // Dropdown options
  const dropdownOptions: any = {
    height: heightOptions,
    weight: Array.from({ length: 171 }, (_, i) => {
      const value = 30 + i;
      return { value: value.toString(), label: `${value} kg` };
    }),
    Physical_State: [
      { value: "normal", label: "Normal" },
      { value: "slim", label: "Slim" },
      { value: "challenged", label: "Challenged" },
    ],
    Eating_Habits: [
      { value: "vegetarian", label: "Vegetarian" },
      { value: "nonVegetarian", label: "Non-Vegetarian" },
      { value: "eggetarian", label: "Eggetarian" },
    ],
    Smoking_Habits: [
      { value: "no", label: "No" },
      { value: "occasionally", label: "Occasional" },
      { value: "yes", label: "Yes" },
    ],
    Drinking_Habits: [
      { value: "no", label: "No" },
      { value: "occasionally", label: "Occasional" },
      { value: "yes", label: "Yes" },
    ],
  };

  interface ModalData {
    key: any;
    options: DropdownOption[];
  }

  // Modal handlers
  const openModal = (key: keyof typeof dropdownOptions) =>
    setModalData({ key, options: dropdownOptions[key] });
  const closeModal = () => {
    setModalData(null);
    setSearchTerm("");
  };

  const handleSelect = (option: DropdownOption) => {
    if (!modalData) return;
    setSelectedOptions((prev) => ({ ...prev, [modalData.key]: option }));
    closeModal();
  };

  // Form data
  const [formData, setFormData] = useState({
    fullName: "",
    dateOfBirth: getDefaultDate(),
    time: getCurrentTime(),
    maritalStatus: "neverMarried",
    maleKids: "",
    femaleKids: "",
    hasRelatives: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!profileId) return;

    const fetchStep2Data = async () => {
      try {
        const res = await getRequest(`/bureau/profile/${profileId.martialId}`);
        if (!res?.data) return;
        const data = res.data;
        setFormData((prev) => ({
          ...prev,
          dateOfBirth: data.dateOfBirth && data.dateOfBirth.split("T")[0],
          time: data.time || prev.time,
          maritalStatus: data.maritalStatus || prev.maritalStatus,
          maleKids: data.maleKids ?? "",
          femaleKids: data.femaleKids ?? "",
          hasRelatives: data.hasRelatives ?? "",
        }));
        setSelectedOptions((prev) => ({
          ...prev,
          height: data.height
            ? { value: data.height, label: `${data.height} ft` }
            : prev.height,
          weight: data.weight
            ? { value: data.weight, label: `${data.weight} kg` }
            : prev.weight,
          Physical_State: data.physicalState
            ? {
                value: data.physicalState,
                label:
                  data.physicalState.charAt(0).toUpperCase() +
                  data.physicalState.slice(1),
              }
            : prev.Physical_State,
          Eating_Habits: data.eatingHabits
            ? {
                value: data.eatingHabits,
                label: data.eatingHabits.replace(/([A-Z])/g, " $1"),
              }
            : prev.Eating_Habits,
          Smoking_Habits: data.smokingHabits
            ? {
                value: data.smokingHabits,
                label:
                  data.smokingHabits === "no"
                    ? "No"
                    : data.smokingHabits === "yes"
                      ? "Yes"
                      : "Occasional",
              }
            : prev.Smoking_Habits,
          Drinking_Habits: data.drinkingHabits
            ? {
                value: data.drinkingHabits,
                label:
                  data.drinkingHabits === "no"
                    ? "No"
                    : data.drinkingHabits === "yes"
                      ? "Yes"
                      : "Occasional",
              }
            : prev.Drinking_Habits,
        }));
      } catch (err) {
        console.error("Failed to load Step2 data", err);
      }
    };
    if (bureauId) fetchStep2Data();
  }, [profileId]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;

    if (!profileId?.userId) {
      console.error("Step2 submit blocked: missing profileId.userId", {
        profileId,
      });

      alert("Profile details are missing. Please go back and try again.");
      return;
    }

    setIsSubmitting(true);

    const formattedOptions = {
      height: selectedOptions.height?.value || "",
      weight: selectedOptions.weight?.value || "",
      physicalState: selectedOptions.Physical_State?.value || "",
      eatingHabits: selectedOptions.Eating_Habits?.value || "",
      smokingHabits: selectedOptions.Smoking_Habits?.value || "",
      drinkingHabits: selectedOptions.Drinking_Habits?.value || "",
    };

    const formDataToSend = {
      ...formData,
      hasRelatives: formData.hasRelatives || undefined,
      maleKids: formData.maleKids || undefined,
      femaleKids: formData.femaleKids || undefined,
      ...formattedOptions,
      step1: 1,
      step2: 1,
    };

    try {
      const response = await putRequest(
        `/bureau/profile/${profileId.userId}`,
        formDataToSend,
      );

      if (!response?.success) {
        alert(response?.message || "Failed to update details.");
        return;
      }

      console.log("BEFORE COMPLETED STEP");

      setCompletedSteps?.((prev) => {
        console.log("INSIDE COMPLETED STEPS CALLBACK", prev);
        return prev.includes(currentStep) ? prev : [...prev, currentStep];
      });

      console.log("AFTER COMPLETED STEP");

      console.log("BEFORE CURRENT STEP");

      setCurrentStep(3);

      console.log("AFTER CURRENT STEP");
    } catch (error) {
      console.error("Step2 submission failed:", error);
      alert("An error occurred while updating details.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    profileId,
    selectedOptions,
    formData,
    currentStep,
    setCompletedSteps,
    setCurrentStep,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="p-6 md:p-8">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* Two-column grid for better spacing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Date of Birth */}
          <div>
            <label className="block text-red-500 font-medium mb-2">
              {t("date_of_birth")}
            </label>
            <input
              type="date"
              name="dateOfBirth"
              max={getMaxDate()}
              value={formData.dateOfBirth}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-400"
            />
          </div>

          {/* Birth Time */}
          <div>
            <label className="block text-red-500 font-medium mb-2">
              {t("birth_time")}
            </label>
            <input
              type="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-400"
            />
          </div>

          {/* Marital Status */}
          <div>
            <label className="block text-red-500 font-medium mb-2">
              {t("marital_status")}
            </label>
            <select
              name="maritalStatus"
              value={formData.maritalStatus}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-400"
            >
              <option value="neverMarried">Never Married</option>
              <option value="awaitingDivorce">Awaiting Divorce</option>
              <option value="divorced">Divorced</option>
              <option value="widow">Widow</option>
            </select>
          </div>

          {/* Kids Info */}
          {formData.maritalStatus !== "neverMarried" && (
            <>
              <div>
                <label className="block text-red-500 font-medium mb-2">
                  {t("male_kids")}
                </label>
                <select
                  name="maleKids"
                  value={formData.maleKids}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-400"
                >
                  <option value="">Select</option>
                  {[...Array(11)].map((_, i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-red-500 font-medium mb-2">
                  {t("female_kids")}
                </label>
                <select
                  name="femaleKids"
                  value={formData.femaleKids}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-400"
                >
                  <option value="">Select</option>
                  {[...Array(11)].map((_, i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-red-500 font-medium mb-2">
                  {t("are_children_with_you")}
                </label>
                <select
                  name="hasRelatives"
                  value={formData.hasRelatives}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-400"
                >
                  <option value="">{t("select")}</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </>
          )}

          {/* Dropdown Options */}
          {(
            Object.keys(dropdownOptions) as (keyof typeof dropdownOptions)[]
          ).map((key: any) => (
            <div key={key}>
              <label className="block text-red-500 font-medium mb-2 capitalize">
                {t(key.replace(/_/g, " "))}
              </label>
              <div
                onClick={() => openModal(key)}
                className="flex items-center justify-between border border-gray-300 px-4 py-3 rounded-md cursor-pointer hover:shadow-sm transition"
              >
                <span className="text-gray-700">
                  {selectedOptions[key]?.label || `Select ${key}`}
                </span>
                <RiArrowRightSLine className="text-xl text-gray-500" />
              </div>
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div className="flex flex-col-reverse sm:flex-row gap-4 pt-8">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all shadow-md ${
              isSubmitting
                ? "cursor-not-allowed bg-gray-400 text-white"
                : "bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700"
            }`}
          >
            {isSubmitting
              ? "Saving..."
              : currentStep === steps.length
                ? "Submit"
                : "Next Step"}
          </button>
        </div>
      </form>

      {/* Modal */}
      {modalData && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full shadow-lg m-5">
            <h2 className="text-lg font-bold mb-4 text-center capitalize">
              Select {modalData.key.replace(/_/g, " ")}
            </h2>
            <input
              type="text"
              placeholder={`Search ${modalData.key}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 mb-4 border border-gray-300 rounded focus:ring-2 focus:ring-pink-400"
            />
            <div className="h-64 overflow-y-auto">
              {modalData.options
                .filter((opt) =>
                  opt.label.toLowerCase().includes(searchTerm.toLowerCase()),
                )
                .map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleSelect(opt)}
                    className="block w-full text-left px-4 py-4 hover:bg-pink-50 rounded transition"
                  >
                    {opt.label}
                  </button>
                ))}
            </div>
            <button
              onClick={closeModal}
              className="mt-4 w-full px-4 py-2 bg-pink-500 text-white rounded hover:bg-pink-600 transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step2;
